import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { ReactNode } from 'react';

import { MOBILE, SHRINKABLE } from '../../responsive';

const useStyles = makeStyles({
  /**
   * Fluent's `medium` drawer is a FIXED 592px, and `large` is wider still — both
   * are wider than any phone, so on mobile the blade ran off the right edge and
   * took its buttons with it (Michael, 2026-09-10: "cutting off the right side").
   * A drawer is already a full-screen surface conceptually; below the breakpoint
   * it simply becomes one.
   */
  drawer: {
    [MOBILE]: {
      width: '100vw',
      maxWidth: '100vw',
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    // Content inside a blade (tables, long names) must scroll INSIDE it rather
    // than widening it — see SHRINKABLE for why min-width:0 is the fix.
    ...SHRINKABLE,
  },
});

/** Width preset for the sliding blade. */
export type BladeSize = 'small' | 'medium' | 'large';

export interface SideBladeProps {
  /** Whether the blade is open. Controlled by the consumer. */
  open: boolean;
  /** Called when the blade requests to close (Close button, Esc, or overlay click). */
  onClose: () => void;
  /** Blade title shown in the header. */
  title: ReactNode;
  /** Blade content. */
  children: ReactNode;
  /** Width preset; defaults to `medium`. */
  size?: BladeSize;
}

/**
 * Right-sliding blade panel for a focused create/detail surface over the current
 * page. Wraps Fluent's {@link OverlayDrawer} (position `end`) with a titled
 * header and a text close affordance (web-app does not depend on
 * `@fluentui/react-icons`). Esc-to-close and overlay light-dismiss route through
 * {@link SideBladeProps.onClose}.
 */
export function SideBlade({
  open,
  onClose,
  title,
  children,
  size = 'medium',
}: SideBladeProps): JSX.Element {
  const styles = useStyles();

  return (
    <OverlayDrawer
      open={open}
      position="end"
      size={size}
      className={styles.drawer}
      onOpenChange={(_event, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button appearance="subtle" aria-label="Close panel" onClick={onClose}>
              Close
            </Button>
          }
        >
          {title}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.body}>{children}</div>
      </DrawerBody>
    </OverlayDrawer>
  );
}
